import { create } from 'zustand'
import { getCookie, removeCookie, setCookie } from '@/lib/cookies'
import { type AuthUser } from '@/features/auth/types'

const ACCESS_TOKEN_KEY = 'app_access_token'
const REFRESH_TOKEN_KEY = 'app_refresh_token'

type AuthTokens = {
  accessToken: string
  refreshToken?: string | null
  preserveSession?: boolean
}

type AuthSlice = {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  sessionVersion: number
  setUser: (user: AuthUser | null) => void
  setTokens: (tokens: AuthTokens) => void
  reset: () => void
}

interface AuthState {
  auth: AuthSlice
}

const readCookie = (key: string): string | null => {
  const raw = getCookie(key)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

export const useAuthStore = create<AuthState>()((set) => {
  const initialAccessToken = readCookie(ACCESS_TOKEN_KEY)
  const initialRefreshToken = readCookie(REFRESH_TOKEN_KEY)

  return {
    auth: {
      user: null,
      accessToken: initialAccessToken,
      refreshToken: initialRefreshToken,
      sessionVersion: 0,
      setUser: (user) =>
        set((state) => ({ ...state, auth: { ...state.auth, user } })),
      setTokens: ({
        accessToken,
        refreshToken = null,
        preserveSession = false,
      }) =>
        set((state) => {
          if (accessToken) {
            setCookie(ACCESS_TOKEN_KEY, accessToken)
          } else {
            removeCookie(ACCESS_TOKEN_KEY)
          }

          if (refreshToken) {
            setCookie(REFRESH_TOKEN_KEY, refreshToken)
          } else {
            removeCookie(REFRESH_TOKEN_KEY)
          }

          return {
            ...state,
            auth: {
              ...state.auth,
              accessToken: accessToken || null,
              refreshToken: refreshToken || null,
              sessionVersion:
                state.auth.sessionVersion + (preserveSession ? 0 : 1),
            },
          }
        }),
      reset: () =>
        set((state) => {
          removeCookie(ACCESS_TOKEN_KEY)
          removeCookie(REFRESH_TOKEN_KEY)
          return {
            ...state,
            auth: {
              ...state.auth,
              user: null,
              accessToken: null,
              refreshToken: null,
              sessionVersion: state.auth.sessionVersion + 1,
            },
          }
        }),
    },
  }
})

export type { AuthUser } from '@/features/auth/types'
